import { Injectable, Type } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DatasourceConnectionComponent } from "../../datasource-connection/datasource-connection.component";
import { BaseConnectionHandler } from "../classes/base-connection-handler.class";
import { ConfigService } from "../../../services/config.service";
import { CommonService, FeatureFlagService, Message, MessageService, Shared } from "@rubii/frontend-common";
import { BaseDatasourceComponent } from "../base-datasource/base-datasource.component";
import { OutbrainComponent } from "../outbrain/outbrain.component";
import { VistarComponent } from "../vistar/vistar.component";
import { QuantcastComponent } from "../quantcast/quantcast.component";
import { MediaMathComponent } from "../media-math/media-math.component";
import { TtdComponent } from "../ttd/ttd.component";
import { XandrComponent } from "../xandr/xandr.component";
import { ZemantaComponent } from "../zemanta/zemanta.component";
import { AmobeeComponent } from "../amobee/amobee.component";
import { IasComponent } from "../ias/ias.component";
import { DoubleVerifyComponent } from "../double-verify/double-verify.component";
import { AdobeComponent } from "../adobe/adobe.component";
import { NielsenComponent } from "../nielsen/nielsen.component";
import { SizmekComponent } from "../sizmek/sizmek.component";
import { PinterestComponent } from "../pinterest/pinterest.component";
import { VerizonComponent } from "../verizon/verizon.component";
import { AdobeSearchComponent } from "../adobe-search/adobe-search.component";
import { TaboolaComponent } from "../taboola/taboola.component";
import { DataStudioComponent } from "../data-studio/data-studio.component";
import { LifesightComponent } from "../lifesight/lifesight.component";
import { StackadaptComponent } from "../stackadapt/stackadapt.component";
import { ChatgptComponent } from "../chatgpt/chatgpt.component";
import { BlisComponent } from "../blis/blis.component";
import { MoatComponent } from "../moat/moat.component";
import { RoktComponent } from "../rokt/rokt.component";
import { ClientService } from "../../../services/client.service";
import { HttpException } from "@rubii/share/src/classes";
import { ShopifyComponent } from "../shopify/shopify.component";
import { AdformComponent } from "../adform/adform.component";
import { KlaviyoComponent } from "../klaviyo/klaviyo.component";import { PubmaticComponent } from '../pubmatic/pubmatic.component';import { ChannelFactoryComponent } from "../channel-factory/channel-factory.component";
import { CommonPopupComponent } from '../_common-popup/common-popup.component';
import * as shared from '@rubii/share';
import { featureXero, xeroEvents } from "../../../constants/features";
import { Scope3Component } from '../scope3/scope3.component';
import { XyzComponent } from "../xyz/xyz.component";
import { EntraComponent } from "../entra/entra.component";
import { GoogleSsoComponent } from "../google-sso/google-sso.component";
import { SeedoohComponent } from "../seedooh/seedooh.component";

declare var amazon: any;

@Injectable({
    providedIn: "root",
})
export class DatasourceService {
    url: string;

    constructor(
        private dialog: MatDialog,
        private config: ConfigService,
        private feature: FeatureFlagService,
        private client: ClientService,
    ) {
        const urls = window.location.href.split("//");
        const protocol = urls[0] + "//";
        const host = urls[1].split("/")[0];
        this.url = protocol + host + "/account/settings";
    }

    getHandler(shortName: string): BaseConnectionHandler {
        return this[shortName];
    }

    // load dynamic components
    loadDatasource(
        dataSourceClass: Type<BaseDatasourceComponent>,
        getDataSources: () => void,
        data?: any,
        connectCallback?: (clientId: string, clientSecret: string) => Promise<0|string>,
    ) {
        let dialogRef = this.dialog.open(DatasourceConnectionComponent, {
            data: {
                dataSourceClass,
                data,
                connectCallback
            },
        });
        dialogRef.afterClosed().subscribe((data) => {
            getDataSources();
            
        });
    }

    apple(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateAppleConsentUrl({ dataSourceId }).then(ret => {
            window.location.href = ret;
        }).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }
    vistar(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(VistarComponent, getDataSources);
    }
    quantcast(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(QuantcastComponent, getDataSources);
    }
    mediamath(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(MediaMathComponent, getDataSources);
    }
    ttd(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(TtdComponent, getDataSources);
    }
    entra(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(EntraComponent, getDataSources, undefined, undefined);
    }
    google_sso(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(GoogleSsoComponent, getDataSources, undefined, undefined);
    }
    xandr(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(XandrComponent, getDataSources);
    }
    zemanta(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(ZemantaComponent, getDataSources);
    }
    amobee(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(AmobeeComponent, getDataSources);
    }
    ias(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(IasComponent, getDataSources);
    }
    scope3(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(Scope3Component, getDataSources);
    }
    blis(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(BlisComponent, getDataSources);
    }
    dv(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(DoubleVerifyComponent, getDataSources);
    }
    adobe(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(AdobeComponent, getDataSources);
    }
    nielsen(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(NielsenComponent, getDataSources);
    }
    sizmek(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(SizmekComponent, getDataSources);
    }
    // pinterest(dataSourceId: number, getDataSources: () => void) {
    //   this.loadDatasource(PinterestComponent, getDataSources);
    // }
    verizon(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(VerizonComponent, getDataSources);
    }
    as(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(AdobeSearchComponent, getDataSources);
    }
    taboola(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(TaboolaComponent, getDataSources);
    }
    outbrain(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(OutbrainComponent, getDataSources);
    }
    lifesight(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(LifesightComponent, getDataSources);
    }

    tiktok(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        // TikTok scopes (incl. identity scope 693: /identity/get|info, /identity/video/get|info) are
        // NOT controlled by the frontend or the auth URL. The Marketing API auth URL has no `scope`
        // param — granted scopes come from the app's "Scope of permission" in the TikTok developer
        // console. To pick up a newly-approved scope (e.g. 693 for Spark ad video), the advertiser
        // simply RECONNECTS here (this re-runs the full authorization); refresh alone won't add it.
        this.client.account.generateTikTokAuthUrl({ dataSourceId }).then(ret => window.location.href = ret as string)
            .catch(e => {
                openSnackBarWarning(snackBar, (e as HttpException).message, action);
                getDataSources();
            });
    }

    datastudio(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close",
        _facebookSdk: any,
        dataSourceLogo: string,
    ) {
        this.client.user.userDataStudioToken().then(ret => {
            let dialogRef = this.dialog.open(
                DatasourceConnectionComponent,
                {
                    data: {
                        dataSourceId,
                        dataSourceClass: DataStudioComponent,
                        userProfilesOrAgencies: [],
                        enabledDatasourceId: "",
                        userDataStudioToken: ret,
                        dataSourceLogo,
                    },
                }
            );
            dialogRef.afterClosed().subscribe((data) => {
                console.log("dialog closed");
                getDataSources();
            });
        }).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    snapchat(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateSnapchatConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string)
            .catch(e => {
                openSnackBarWarning(snackBar, (e as HttpException).message, action);
                getDataSources();
            });
    }

    twitter(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.twitterRequestToken().then(ret => window.location.href =
            "https://api.twitter.com/oauth/authenticate?oauth_token=" +
            ret).catch(e => {
                openSnackBarWarning(snackBar, (e as HttpException).message, action);
                getDataSources();
            });
    }

    linkedin() {
        const clientId = this.config.inClientId;

        let redirect_uri = this.url;
        if (location.host.match(/^localhost/)) {
            redirect_uri = `${location.protocol}//${location.host}${location.pathname}`;
        }
        const url =
            "https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=" +
            clientId +
            "&redirect_uri=" +
            encodeURIComponent(redirect_uri + "?type=in") +
            "&scope=r_ads r_ads_reporting r_basicprofile r_organization_social r_organization_admin";
        window.location.href = url;
    }

    amazon(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        const options = {
            scope: "advertising::campaign_management profile",
            response_type: "code",
        };
        amazon.Login.setClientId('amzn1.application-oa2-client.d27c30bfc45c42e1a9602858fdd02814');
        amazon.Login.authorize(options, (response) => {
            console.log('====== response ========');
            console.log(response);
            if (response.error) {
                openSnackBarWarning(snackBar, "Access Denied", action);
                return;
            }

            const input = {
                code: response.code,
            };
            this.client.account.exchangeAmazonAccessToken(input).then(ret => {
                openSnackBar(snackBar, "Amazon connected.", action);
            }).catch(e => {
                openSnackBarWarning(snackBar, (e as HttpException).message, action);
                getDataSources();
            });
        });
        return false;
    }

    fb(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close",
        FB: any,
    ) {
        if (!confirm("Confirm to connect Facebook")) {
            return;
        }
        const self = this;
        FB.login(
            function (response) {
                if (response.status === "connected") {
                    let token = response.authResponse.accessToken;
                    let expireDate = Shared.helpers.UtilHelper.getDateTimeStr(
                        new Date(
                            response.authResponse.data_access_expiration_time *
                            1000
                        )
                    );

                    let input = {
                        token: token,
                        expireDate: expireDate,
                        userId: response.authResponse.userID,
                    };
                    self.client.account.saveFBToken(input).then(ret => {
                        openSnackBar(snackBar, "Facebook connected.", action);
                        getDataSources();
                    }).catch(e => {
                        openSnackBarWarning(snackBar, (e as HttpException).message, action);
                        getDataSources();
                    });
                }
            },
            {
                scope: "public_profile,email,ads_read,ads_management,catalog_management,business_management,pages_show_list,pages_read_engagement,pages_read_user_content,read_insights,instagram_basic,instagram_manage_insights,ads_mcp_management",
            }
        );
    }

    yahoo(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateYahooConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    reddit(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateRedditConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    spotify(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateSpotifyConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    tiktokorganic(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateTiktokOrganicConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    hubspot(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateHubspotConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    pinterest(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generatePinterestConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    xero(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.feature.trigger(featureXero, xeroEvents.Connecting, {});
        this.client.account.generateXeroConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    criteo(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateCriteoConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    stackadapt(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(StackadaptComponent, getDataSources);
    }

    chatgpt(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(ChatgptComponent, getDataSources);
    }

    bing(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateBingConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }

    rea(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(CommonPopupComponent, getDataSources, { key: 'Client Id', secret: 'Client Secret', platform: 'REA' }, async (clientId: string, clientSecret: string) => {
            try {
                await this.client.account.fetchAndSaveREAConfig({
                    clientId,
                    clientSecret,
                });   
                return 0;
            } catch (error) {
                return error?.message as string;
            }
        });
    }

    teads(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(CommonPopupComponent, getDataSources, { key: 'Email', secret: 'API Key', platform: 'Teads' }, async (clientId: string, clientSecret: string) => {
            try {
                await this.client.account.fetchAndSaveTeadsConfig({
                    username: clientId,
                    apiKey: clientSecret,
                });
                return 0;
            } catch (error) {
                return error?.message as string;
            }
        });
    }

    // connect to google
    dv360(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }

    gam(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }

    dcm(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    sa360(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    gads(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    seo(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    youtubeorganic(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    ga(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    ga4(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.connectToGoogle(
            dataSourceId,
            getDataSources,
            openSnackBarWarning,
            snackBar,
            action
        );
    }
    connectToGoogle(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateGoogleConsentUrl({ dataSourceId }).then(ret => window.location.href = ret).catch(e => {
            openSnackBarWarning(snackBar, (e as HttpException).message, action);
            getDataSources();
        });
    }
    // google end

    moat(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(MoatComponent, getDataSources);
    }

    rokt(datasourceId: number, getDataSources: () => void) {
        this.loadDatasource(RoktComponent, getDataSources);
    }

    shopify(datasourceId: number, getDataSources: () => void) {
        this.loadDatasource(ShopifyComponent, getDataSources);
    }

    klaviyo(datasourceId: number, getDataSources: () => void) {
        this.loadDatasource(KlaviyoComponent, getDataSources);
    }

    pubmatic(datasourceId: number, getDataSources: () => void) {
        this.loadDatasource(PubmaticComponent, getDataSources);
    }

    adform(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(AdformComponent, getDataSources);
    }

    cf(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(ChannelFactoryComponent, getDataSources);
    }

    gumgum(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(XyzComponent, getDataSources);
    }

    yahoojp(
        dataSourceId: number,
        getDataSources: () => void,
        openSnackBar: (snackBar: any, message: string, action?: string) => void,
        openSnackBarWarning: (snackBar: any, message: string, action?: string) => void,
        snackBar: any,
        action: string = "close"
    ) {
        this.client.account.generateYahoojpConsentUrl({ dataSourceId }).then(ret => window.location.href = ret as string)
            .catch(e => {
                openSnackBarWarning(snackBar, (e as HttpException).message, action);
                getDataSources();
            });
    }

    seedooh(dataSourceId: number, getDataSources: () => void) {
        this.loadDatasource(SeedoohComponent, getDataSources);
    }
}
